import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import Avatar from '../../components/ui/Avatar';

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Administración' },
  { value: 'seller', label: 'Ventas' },
  { value: 'services', label: 'Servicios' },
  { value: 'installations', label: 'Instalaciones' },
  { value: 'demos', label: 'Demostraciones' },
];

const initialForm = {
  name: '',
  email: '',
  role: ROLE_OPTIONS[0].value,
};

const AdminTeam = () => {
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [inviteLink, setInviteLink] = useState('');
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  const roles = useMemo(() => ROLE_OPTIONS.map((option) => option.value), []);

  const loadMembers = async () => {
    setLoading(true);
    try {
      const [usersSnap, profilesSnap] = await Promise.all([
        getDocs(query(collection(db, 'users'), where('role', 'in', roles))),
        getDocs(collection(db, 'teamProfiles')),
      ]);

      const profilesById = Object.fromEntries(profilesSnap.docs.map((docSnap) => [docSnap.id, docSnap.data() || {}]));
      const loaded = usersSnap.docs.map((docSnap) => {
        const data = docSnap.data();
        const profile = profilesById[docSnap.id] || {};
        const createdAt = data?.inviteSentAt?.toDate?.() || data?.createdAt?.toDate?.() || null;
        return {
          id: docSnap.id,
          ...data,
          name: profile.displayName || data.name || data.displayName || data.email || 'Sin nombre',
          imageUrl: profile.imageUrl || data.imageUrl || data.profileImageUrl || '',
          createdAt,
          profileReady: Boolean(profile.displayName || profile.imageUrl),
        };
      }).filter((member) => member.active !== false && member.status !== 'archived');

      loaded.sort((a, b) => {
        if (!a.createdAt && !b.createdAt) return 0;
        if (!a.createdAt) return 1;
        if (!b.createdAt) return -1;
        return b.createdAt.getTime() - a.createdAt.getTime();
      });

      setMembers(loaded);
    } catch (err) {
      console.error('❌ Error cargando equipo:', err);
      setMessage('No se pudo cargar el equipo.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleInvite = async (event) => {
    event.preventDefault();
    setMessage('');
    setInviteLink('');

    if (!form.name.trim() || !form.email.trim()) {
      setMessage('Nombre y correo son obligatorios.');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(
        'https://us-central1-tarjeta-ad945.cloudfunctions.net/inviteTeamMember',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: form.name.trim(),
            email: form.email.trim(),
            role: form.role,
          }),
        }
      );

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Error al enviar la invitación.');
      }

      setMessage('Invitación enviada correctamente.');
      setInviteLink(result.resetLink || '');
      setForm(initialForm);
      await loadMembers();
    } catch (err) {
      console.error('❌ Error invitando usuario:', err);
      setMessage(err.message || 'No se pudo enviar la invitación.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-semibold text-gray-900">Equipo y accesos</h2>
        <p className="text-gray-500">
          Invita miembros del equipo y envíales el enlace para crear su contraseña.
        </p>
      </div>

      {message && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-blue-800">
          {message}
        </div>
      )}

      {inviteLink && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-emerald-800 text-sm">
          Enlace generado: <span className="break-all">{inviteLink}</span>
        </div>
      )}

      <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Nueva invitación</h3>
        <form className="grid gap-4 md:grid-cols-2" onSubmit={handleInvite}>
          <input
            type="text"
            name="name"
            placeholder="Nombre completo"
            value={form.name}
            onChange={handleChange}
            className="rounded-lg border border-gray-300 px-3 py-2"
          />
          <input
            type="email"
            name="email"
            placeholder="Correo electrónico"
            value={form.email}
            onChange={handleChange}
            className="rounded-lg border border-gray-300 px-3 py-2"
          />
          <select
            name="role"
            value={form.role}
            onChange={handleChange}
            className="rounded-lg border border-gray-300 px-3 py-2"
          >
            {ROLE_OPTIONS.map((role) => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </select>
          <div>
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-orange-500 px-4 py-2 font-medium text-white hover:bg-orange-600 disabled:opacity-60"
            >
              {saving ? 'Enviando...' : 'Enviar invitación'}
            </button>
          </div>
        </form>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Equipo activo</h3>
          {loading && <span className="text-sm text-gray-400">Cargando...</span>}
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-4 py-2 text-left font-semibold">Nombre</th>
                <th className="px-4 py-2 text-left font-semibold">Correo</th>
                <th className="px-4 py-2 text-left font-semibold">Área</th>
                <th className="px-4 py-2 text-left font-semibold">Perfil</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {members.map((member) => (
                <tr key={member.id}>
                  <td className="px-4 py-3 font-medium text-gray-900">
                    <div className="flex items-center gap-2">
                      <Avatar src={member.imageUrl} name={member.name || member.email} size="sm" />
                      <span>{member.name || 'Sin nombre'}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{member.email}</td>
                  <td className="px-4 py-3 text-gray-600">{member.roleLabel || member.role}</td>
                  <td className="px-4 py-3">
                    {member.profileReady ? (
                      <span className="text-emerald-600 font-semibold">Listo</span>
                    ) : (
                      <span className="text-gray-400">Pendiente</span>
                    )}
                  </td>
                </tr>
              ))}
              {!loading && members.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-gray-500">
                    No hay miembros registrados todavía.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default AdminTeam;
